import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";

import Text from "components/common/Text";
import ErrorsIndicatorList from "components/common/ErrorsIndicatorList/ErrorsIndicatorList";
import Button from "components/common/Button";
import TermsAndPolicy from "components/common/TermAndPolicy/Terms-And-Policy";

import { LOGIN_ERRORS_INDICATORS } from "constants";
import { HAS_UPPER_AND_LOWER_LETTER, HAS_NUMBER, HAS_SPECIAL_CHAR } from "constants/regexPatterns";
import { LOGIN_ROUTE } from "constants/routes";

import MailOutlineIcon from "@mui/icons-material/MailOutline";

import "./setPassword.scss";

function SetPasswordPage({
  resetPasswordHandler = () => {},
  isForgotPasswordFlow = false,
  userName = "",
  email = "",
  isSubmitting = false,
  resetPasswordWrapperHeightClass = "reset-password-wrapper-height",
  showTermsAndPolicy = true,
  userType = "Customer",
}) {
  const navigateTo = useNavigate();

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });
  const { handleSubmit, watch } = methods;

  const [errorsListNewPass, setErrorsListNewPass] = useState(LOGIN_ERRORS_INDICATORS);
  const [errorsListConfirmPass, setErrorsListConfirmPass] = useState([{ message: "Passwords match" }]);

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (newPassword.trim() !== "" || confirmPassword !== "") {
      const updatedErrorsList = LOGIN_ERRORS_INDICATORS?.map((error) => {
        switch (error.id) {
          case 1:
            return { ...error, isChecked: newPassword?.length >= 8 };
          case 2:
            return { ...error, isChecked: HAS_UPPER_AND_LOWER_LETTER.test(newPassword) };
          case 3:
            return { ...error, isChecked: HAS_NUMBER.test(newPassword) };
          case 4:
            return { ...error, isChecked: HAS_SPECIAL_CHAR.test(newPassword) };
          default:
            return error;
        }
      });
      setErrorsListNewPass(updatedErrorsList);

      setErrorsListConfirmPass([
        {
          message: "Passwords match",
          isChecked: !!confirmPassword && newPassword === confirmPassword,
        },
      ]);
    }
  }, [newPassword, confirmPassword]);

  const onSubmit = (data) => {
    resetPasswordHandler(data);
  };

  const isButtonDisabled =
    !errorsListNewPass?.every((e) => e.isChecked) || !errorsListConfirmPass.every((e) => e.isChecked);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="password-form">
        <div className={`reset-password-wrapper ${resetPasswordWrapperHeightClass}`}>
          <div className="top-section">
            <div className="welcome-text">
              <p className="heading-h1-semibold color-text-heading-primary">Welcome, {userName}</p>
              <p className="para-body-m-regular color-text-body-primary" data-testid="welcome-message">
                {isForgotPasswordFlow
                  ? "Please set up your new password and you are good to go."
                  : "Set up your password to begin your journey with us."}
              </p>
            </div>

            <div className="email-section">
              <div className="email-icon">
                <MailOutlineIcon />
              </div>
              <div className="email-info">
                <p className="heading-h5-regular text-headings-secondary">Your email address</p>
                <p className="para-body-m-medium color-text-body-secondary">{email}</p>
              </div>
            </div>
          </div>

          <Text
            label={isForgotPasswordFlow ? "New password" : "Password"}
            name="newPassword"
            type="password"
            showPasswordEye
            data-testid="new-password-input"
            ref={methods.register}
          />

          <ErrorsIndicatorList errors={errorsListNewPass} />

          <Text
            label={isForgotPasswordFlow ? "Confirm New Password" : "Confirm Password"}
            name="confirmPassword"
            type="password"
            data-testid="confirm-password-input"
            showPasswordEye
            ref={methods.register}
          />

          <ErrorsIndicatorList errors={errorsListConfirmPass} />

          <div className="button-container">
            <Button
              isDisabled={isButtonDisabled}
              type="submit"
              variant="contained"
              color="primary"
              data-testid="submit-button"
              label={isForgotPasswordFlow ? "Reset Password" : "Create password and login"}
              isFullWidth
              isLoading={isSubmitting}
            />
            {isForgotPasswordFlow && (
              <Button
                onClick={() => navigateTo(LOGIN_ROUTE, { state: { userType } })}
                variant="text"
                color="primary"
                label="Return to login"
                isFullWidth
              />
            )}
          </div>
          {showTermsAndPolicy && <TermsAndPolicy />}
        </div>
      </form>
    </FormProvider>
  );
}

export default SetPasswordPage;
